import { cloneDeep } from 'lodash';

import { DBDocSchema } from '../../../generic/db-doc/db-doc-schema';
import { GenericDisplayable } from '../../../generic/displayable/generic-displayable';
import { VoiceResponseCommand } from '../../../voice-response-command/generic/voice-response-command';
import { CalleeEntity } from '../../callee-entity/callee-entity';
import { CallListCommunicationWorkflowConfig } from '../communication-workflow-config/call-list-communication-workflow-config/call-list-communication-workflow-config';
import { CallListExtensionConfig } from '../extension-config/call-list-extension-config/call-list-extension-config';
import { CallListOfferConfig } from '../offer-config/call-list-offer-config/call-list-offer-config';

import { CallListConstructor } from './call-list-constructor';
import { CallListSchema } from './call-list-schema';
import { defaultCallListOfferConfig } from './constants';

export class CallList extends GenericDisplayable {
  /////////////////////////////////////////////////////////////////////////////
  // Variables
  /////////////////////////////////////////////////////////////////////////////

  protected calleeEntities!: CalleeEntity[];

  protected confirmJoinIVRId!: string | undefined;

  protected capacity!: number;

  protected onNoUserLeftToDialModIncCallCommands!: VoiceResponseCommand[];

  protected callerHangupOnStarCommands?: VoiceResponseCommand[];

  protected calleeHangupOnStarCommands?: VoiceResponseCommand[];

  protected dialTimeoutSeconds!: number;

  protected pIIRetentionDuration!: string;

  protected offerConfig!: CallListOfferConfig;

  protected extensionConfig!: CallListExtensionConfig;

  protected communicationWorkflowConfig: CallListCommunicationWorkflowConfig | undefined;

  /////////////////////////////////////////////////////////////////////////////
  // Constructor
  /////////////////////////////////////////////////////////////////////////////

  constructor(parameters: CallListConstructor) {
    super(parameters);
    this.offerConfig = parameters.offerConfig ?? defaultCallListOfferConfig;
  }

  /////////////////////////////////////////////////////////////////////////////
  // Deserialize
  /////////////////////////////////////////////////////////////////////////////

  /**
   * This static function is private, and meant to be called only by
   * SerializableObject, and subclasses
   *
   * @param validationResult
   */
  protected static _deserialize(validationResult: import('joi').ValidationResult): CallList {
    return new CallList(super._deserialize(validationResult));
  }

  /////////////////////////////////////////////////////////////////////////////
  // Serialize
  /////////////////////////////////////////////////////////////////////////////

  public serialize() {
    return super.serialize(CallList.getSchema());
  }

  /////////////////////////////////////////////////////////////////////////////
  // Schema
  /////////////////////////////////////////////////////////////////////////////

  public static getSchema(): DBDocSchema {
    return new CallListSchema();
  }

  /////////////////////////////////////////////////////////////////////////////
  // Getters
  /////////////////////////////////////////////////////////////////////////////

  public getCalleeEntities() {
    return cloneDeep(this.calleeEntities) ?? [];
  }

  public getCapacity(): number {
    return cloneDeep(this.capacity);
  }

  public getCommunicationWorkflowConfig() {
    return cloneDeep(this.communicationWorkflowConfig);
  }

  public getConfirmJoinIVRId() {
    return cloneDeep(this.confirmJoinIVRId);
  }

  public getOnNoUserLeftToDialModIncCallCommands() {
    return cloneDeep(this.onNoUserLeftToDialModIncCallCommands);
  }

  public getCallerHangupOnStarCommands() {
    return cloneDeep(this.callerHangupOnStarCommands ?? []);
  }

  public getCalleeHangupOnStarCommands() {
    return cloneDeep(this.calleeHangupOnStarCommands ?? []);
  }

  public getDialTimeoutSeconds() {
    return cloneDeep(this.dialTimeoutSeconds);
  }

  public getPIIRetentionDuration() {
    return cloneDeep(this.pIIRetentionDuration);
  }

  public getOfferConfig() {
    return cloneDeep(this.offerConfig);
  }

  public getExtensionConfig() {
    return cloneDeep(this.extensionConfig);
  }
}
